// src/config/firebase.js
import { initializeApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import { getAnalytics } from 'firebase/analytics';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Firebase configuration - Environment-aware with fallbacks
const firebaseConfig = process.env.NODE_ENV === 'production' ? {
  // Production configuration
  apiKey: "AIzaSyAq8HWEBwip1joORYXSkg4Ug4uKp56qBSM",
  authDomain: "healehub-production.firebaseapp.com",
  projectId: "healehub-production",
  storageBucket: "healehub-production.appspot.com",
  messagingSenderId: "483849814054",
  appId: "1:483849814054:web:0178aebf6c856ed970e6e7",
  measurementId: "G-00LCVF007D"
} : {
  // Development configuration
  apiKey: "AIzaSyDnDUNdokw1ll6WI-smJXPFYyLW-NiErv4",
  authDomain: "healehub-dev.firebaseapp.com",
  projectId: "healehub-dev",
  storageBucket: "healehub-dev.firebasestorage.app",
  messagingSenderId: "551267618994",
  appId: "1:551267618994:web:af94c481343b48103bf9ab",
  measurementId: "G-00LCVF007D"
};

console.log('Firebase config loaded:', firebaseConfig);
// Expose for runtime debug in browser console
if (typeof window !== 'undefined') {
  window.__FIREBASE_CFG__ = firebaseConfig;
}

// Initialize Firebase with improved error handling
let app = null;
let db = null;
let auth = null;
let storage = null;
let analytics = null;
let functions = null;

try {
  console.log('Attempting to initialize Firebase...');

  // Validate config first
  if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
    throw new Error('Firebase configuration is incomplete');
  }

  app = initializeApp(firebaseConfig);
  console.log('✅ Firebase app initialized');

  db = getFirestore(app);
  console.log('✅ Firestore initialized');

  // Only initialize auth if we have a valid app
  if (app) {
    auth = getAuth(app);
    console.log('✅ Firebase Auth initialized');

    storage = getStorage(app);
    console.log('✅ Firebase Storage initialized');

    // Initialize functions with correct region for environment
    if (process.env.NODE_ENV === 'production') {
      functions = getFunctions(app, 'asia-southeast1');
      console.log('✅ Firebase Functions initialized for PRODUCTION (asia-southeast1)');
    } else {
      functions = getFunctions(app, 'asia-southeast1');
      console.log('✅ Firebase Functions initialized for DEV (asia-southeast1)');
    }
  }

  // Initialize Analytics only in production
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'production' && app) {
    analytics = getAnalytics(app);
    console.log('✅ Firebase Analytics initialized');
  }

  console.log('🎉 Firebase initialization complete!');

} catch (error) {
  console.warn('⚠️ Firebase initialization failed:', error.message);
  console.log('📱 Falling back to localStorage mode');

  // Ensure all Firebase services are null on failure
  app = null;
  db = null;
  auth = null;
  storage = null;
  analytics = null;
  functions = null;

  // Don't throw the error - just log it and continue
}

// Debug helpers: expose claim refresh and token utilities in browser console
if (typeof window !== 'undefined') {
  // Core handles for debugging
  window.__AUTH__ = auth;
  window.__DB__ = db;
  window.__FUNCTIONS__ = functions;

  window.__forceTokenRefresh = async () => {
    try {
      if (!auth || !auth.currentUser) throw new Error('No current user');
      const res = await auth.currentUser.getIdToken(true);
      console.log('✅ Token refreshed');
      return res;
    } catch (e) {
      console.error('❌ __forceTokenRefresh error:', e);
      throw e;
    }
  };

  window.__getTokenClaims = async () => {
    try {
      if (!auth || !auth.currentUser) throw new Error('No current user');
      const tr = await auth.currentUser.getIdTokenResult(true);
      console.log('🔍 Claims:', tr.claims);
      return tr.claims;
    } catch (e) {
      console.error('❌ __getTokenClaims error:', e);
      throw e;
    }
  };

  window.__refreshClaims = async () => {
    try {
      if (!functions) throw new Error('Functions not initialized');
      const call = httpsCallable(functions, 'refreshTenantClaims');
      const resp = await call({});
      console.log('✅ refreshTenantClaims result:', resp?.data);
      // Force token refresh after updating claims
      if (auth && auth.currentUser) {
        await auth.currentUser.getIdToken(true);
      }
      return resp?.data;
    } catch (e) {
      console.error('❌ __refreshClaims error:', e);
      throw e;
    }
  };
}

export { db, auth, storage, analytics, functions };
export default app;
